import { storefrontClient } from './storeFrontClient';

interface InventoryCheckResult {
  variantId: string;
  availableQuantity: number;
  isAvailable: boolean;
}

export async function checkVariantInventory(
  variantId: string,
): Promise<InventoryCheckResult | null> {
  const query = `
    query getVariantInventory($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          id
          availableForSale
          quantityAvailable
        }
      }
    }
  `;

  try {
    const response = await storefrontClient.post('', {
      query,
      variables: { id: variantId },
    });

    if (response.data.errors) {
      console.error('Shopify GraphQL errors:', response.data.errors);
      return null;
    }

    const variant = response.data.data.node;

    if (!variant) {
      console.error('Variant not found:', variantId);
      return null;
    }

    return {
      variantId,
      availableQuantity: variant.quantityAvailable ?? 0,
      isAvailable: variant.availableForSale ?? false,
    };
  } catch (error) {
    console.error('Error checking variant inventory:', error);
    return null;
  }
}

export async function checkCartInventory(
  items: Array<{ variantId: string; quantity: number; slug: string }>,
): Promise<{
  allInStock: boolean;
  outOfStockItems: Array<{
    slug: string;
    variantId: string;
    requested: number;
    available: number;
  }>;
}> {
  const inventoryChecks = await Promise.all(
    items.map(async (item) => {
      const inventory = await checkVariantInventory(item.variantId);

      if (!inventory) {
        console.warn(`Could not check inventory for ${item.variantId}, assuming available`);
        return { item, isInStock: true, outOfStockInfo: null };
      }

      const isInStock = inventory.isAvailable && inventory.availableQuantity >= item.quantity;

      return {
        item,
        isInStock,
        outOfStockInfo: !isInStock
          ? {
              slug: item.slug,
              variantId: item.variantId,
              requested: item.quantity,
              available: inventory.availableQuantity,
            }
          : null,
      };
    }),
  );

  const outOfStockItems = inventoryChecks
    .filter((check) => check.outOfStockInfo)
    .map((check) => check.outOfStockInfo!);

  const allInStock = inventoryChecks.every((check) => check.isInStock);

  return {
    allInStock,
    outOfStockItems,
  };
}
